import { useEffect, useState } from "react";
import { isIOS, isSafari } from "@scrile/tools/dist/lib/browserUtils";
import { MediaDevicePermissions } from "../types";

const PERMISSION_DEVICES: { key: keyof MediaDevicePermissions; name: PermissionName }[] = [
  { key: "camera", name: "camera" as PermissionName },
  { key: "microphone", name: "microphone" as PermissionName },
];

const initialPermissions: MediaDevicePermissions = {
  camera: "granted",
  microphone: "granted",
};

const POLL_INTERVAL = 3000;

export default function useMediaDevicePermissions(onChange?: (permissions: MediaDevicePermissions) => void) {
  const [permissions, setPermissions] = useState<MediaDevicePermissions>(initialPermissions);

  useEffect(() => {
    if (!navigator.permissions?.query) {
      onChange?.(initialPermissions);
      return undefined;
    }

    let cancelled = false;
    const statuses: { status: PermissionStatus; listener: () => void }[] = [];

    const queryPermissions = () =>
      Promise.all(PERMISSION_DEVICES.map(({ name }) => navigator.permissions.query({ name }).catch(() => null)));

    const updatePermission = (key: keyof MediaDevicePermissions, status: PermissionStatus) => {
      setPermissions((prev) => {
        const next = { ...prev, [key]: status.state };
        onChange?.(next);
        return next;
      });
    };

    const applyResults = (results: (PermissionStatus | null)[]) => {
      results.forEach((status, index) => {
        if (!status) return;
        const { key } = PERMISSION_DEVICES[index];
        updatePermission(key, status);
      });
    };

    let pollInterval: ReturnType<typeof setInterval> | undefined;

    queryPermissions().then((results) => {
      if (cancelled) return;
      applyResults(results);

      const hasChangeEvent = !isIOS() && !isSafari();

      if (hasChangeEvent) {
        results.forEach((status, index) => {
          if (!status) return;
          const { key } = PERMISSION_DEVICES[index];
          const listener = () => updatePermission(key, status);
          status.addEventListener("change", listener);
          statuses.push({ status, listener });
        });
      } else {
        // Fallback to polling if change events are not supported (e.g., Safari iOS)
        pollInterval = setInterval(() => {
          queryPermissions().then((freshResults) => {
            if (!cancelled) applyResults(freshResults);
          });
        }, POLL_INTERVAL);
      }
    });

    return () => {
      cancelled = true;
      statuses.forEach(({ status, listener }) => status.removeEventListener("change", listener));
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [onChange]);

  return permissions;
}
